/**
 * useProductFormV3 - Main form state hook for V3 product editor
 *
 * Manages product form state that mirrors ProductV2 structure exactly.
 * Provides generic field setters and axis management.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { arrayMove } from '@dnd-kit/sortable';
import {
  ProductFormStateV3,
  UseProductFormV3Return,
  VariantV3,
  VariantGroupV3,
  AxisValue,
  AxisValuesMap,
  FieldPath,
  AXIS_DEFAULT_VALUE,
  isDefaultAxisValue,
  createEmptyFormState,
  isDimensionValue,
  isColorAxisValue,
  formatAxisValueForDisplay,
  generateVariantKeyFromAxes,
} from '../types';
import type {
  ProductV2,
  ProductVisualization,
  VisualizationType,
} from '../../../../common/models/product-v2.model';
import { createDefaultVisualization } from '../../../../common/models/product-v2.model';
import type {
  ProductFamily,
  VariantAxisConfig,
} from '../../../../common/models/product-family.model';
import { getEnabledVariantAxes } from '../../../../common/models/product-family.model';
import { productV2Api } from '../api/productV2Api';
import { alignLoadedAxes, colorAxisNameOf, colorValueForAxis } from '../utils/variantAxes';
import { knownPatternName } from '../../../../common/utils/pattern-name';
import { inheritedCommerce } from '../utils/pricePackaging';

// ============================================================================
// Types
// ============================================================================

/** Pre-fill data from external sources (e.g., product page scraping) */
export interface InitialProductData {
  name?: string;
  brand?: string;
  description?: string;
  sku?: string;
  productPageUrl?: string;
  /**
   * Product images found on the page, as remote URLs.
   *
   * Carried so the editor can fetch them and seed the variant's plank images.
   * Extracted and previewed all along, then dropped here — the vendor was
   * shown the tile photos the reader had found and then handed an empty
   * uploader, which is the one step the feature exists to save them.
   */
  imageUrls?: string[];
  productType?: string;
  applicationTypes?: string[];
  /** A scraped colour: `{ value, hex }`, or a plain name. */
  color?: { value: string; hex?: string } | string;
  size?: { width: number; height: number; depth?: number; unit?: string };
  pattern?: string;
}

export interface UseProductFormV3Options {
  /** Product UUID for edit mode (undefined for create mode) */
  productId?: string;
  /** Product family configuration */
  family: ProductFamily;
  /** Vendor ID */
  vendorId: string;
  /** Vendor company name (used as default brand in create mode) */
  vendorName?: string;
  /** Pre-fill data for create mode (applied during form initialization, survives re-init) */
  initialData?: InitialProductData;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get a nested value from an object using dot notation path
 */
function getNestedValue<T>(obj: any, path: string): T | undefined {
  const parts = path.split('.');
  let current = obj;

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }
    current = current[part];
  }

  return current as T;
}

/**
 * Set a nested value in an object using dot notation path
 * Returns a new object with the updated value (immutable)
 */
function setNestedValue<T>(obj: T, path: string, value: unknown): T {
  const parts = path.split('.');
  const result = { ...obj } as any;

  let current = result;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    current[part] = { ...current[part] };
    current = current[part];
  }

  current[parts[parts.length - 1]] = value;
  return result;
}

/**
 * Generate cartesian product of arrays
 */
function cartesianProduct<T>(arrays: T[][]): T[][] {
  if (arrays.length === 0) return [[]];

  return arrays.reduce<T[][]>(
    (acc, arr) => acc.flatMap((x) => arr.map((y) => [...x, y])),
    [[]]
  );
}

/**
 * Generate a fresh plankGroupId. Variants of the same product that share
 * grouping-axis values must end up with the same plankGroupId — that is
 * handled by the per-form `plankGroupIdCache` keyed on those axes, not by
 * making this generator deterministic. Sharing across *different* products
 * must never happen, so the id is a fresh uuid.
 */
function generatePlankGroupId(
  _axes: Record<string, AxisValue>,
  _groupingAxes: VariantAxisConfig[]
): string {
  return `material_${uuidv4().replace(/-/g, '')}`;
}

/**
 * Convert ProductV2 to form state
 */
function productToFormState(product: ProductV2): ProductFormStateV3 {
  const { _id, createdAt, updatedAt, ...rest } = product as any;

  const formState: ProductFormStateV3 = {
    ...rest,
    _isDirty: false,
    _originalSnapshot: '',
  };

  formState._originalSnapshot = JSON.stringify(formState);
  return formState;
}

/**
 * Create a stable JSON string that's order-independent
 * Sorts object keys recursively before stringifying
 */
function createStableSnapshot(obj: unknown): string {
  return JSON.stringify(obj, (_, value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Sort object keys for consistent ordering
      const sorted: Record<string, unknown> = {};
      Object.keys(value).sort().forEach(key => {
        sorted[key] = value[key];
      });
      return sorted;
    }
    return value;
  });
}

/**
 * Extract axis values from a list of products (variants)
 */
function extractAxisValuesFromProducts(
  products: ProductV2[],
  enabledAxes: VariantAxisConfig[]
): AxisValuesMap {
  const axisValues: AxisValuesMap = {};

  for (const axis of enabledAxes) {
    const values: AxisValue[] = [];
    const seen = new Set<string>();

    for (const product of products) {
      const value = product.axes?.[axis.name];
      if (value !== undefined && value !== null) {
        // Generate a key for deduplication
        const key = formatAxisValueForDisplay(value as AxisValue);
        if (!seen.has(key)) {
          seen.add(key);
          values.push(value as AxisValue);
        }
      }
    }

    axisValues[axis.name] = values;
  }

  return axisValues;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useProductFormV3(options: UseProductFormV3Options): UseProductFormV3Return {
  const { productId, family, vendorId, vendorName, initialData } = options;

  /** A scraped SKU waiting for the first variant to exist. Used once. */
  const seedSkuRef = useRef<string | null>(null);
  const takeSeedSku = useCallback((): string => {
    const seed = seedSkuRef.current;
    seedSkuRef.current = null;
    return seed ?? '';
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────────────────────────────────

  const [product, setProduct] = useState<ProductFormStateV3 | null>(null);
  // Start loading if we have productId OR if family isn't loaded yet
  const [isLoading, setIsLoading] = useState<boolean>(!!productId || !family);
  const [error, setError] = useState<string | null>(null);

  // Axis values for variant generation
  const [axisValuesMap, setAxisValuesMap] = useState<AxisValuesMap>({});

  // Generated variants
  const [variants, setVariants] = useState<VariantV3[]>([]);

  // Ref to hold current variants for stale closure prevention
  const variantsRef = useRef<VariantV3[]>([]);
  // Keep variantsRef in sync with variants state
  variantsRef.current = variants;

  // Flag to skip variant regeneration right after loading
  const skipNextRegenerationRef = useRef(false);

  // Original state for reset
  const originalProductRef = useRef<ProductFormStateV3 | null>(null);
  const originalAxisValuesRef = useRef<AxisValuesMap>({});
  // Track original variant UUIDs to detect deletions
  const originalVariantUuidsRef = useRef<Set<string>>(new Set());

  // "Draft template": for products loaded with no axes (created by an API
  // integration sync), the backing mongo document exists but has no variant
  // axes yet. When the vendor later enters axis values, regeneration would
  // normally wipe the loaded variant and create fresh ones with new uuids —
  // categorizeVariants would then see the old uuid as deleted and the new
  // ones as created, losing the original product identity. We persist that
  // loaded variant in this ref so the first generated combo can adopt its
  // uuid + snapshot across intermediate regenerations (e.g. while the user
  // is still filling in required axes one at a time).
  const draftTemplateRef = useRef<VariantV3 | null>(null);

  // Track previous axis values so auto-regeneration only adds combinations
  // for newly added axis values (avoids filling in missing combinations
  // when the user has a curated subset of variants).
  const prevAxisValuesRef = useRef<AxisValuesMap>({});

  // Cached raw product data fetched in parallel with family loading
  const rawProductCacheRef = useRef<ProductV2 | null>(null);
  const productFetchedRef = useRef(false);
  // State to trigger family effect when fetch completes
  const [rawProductReady, setRawProductReady] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────
  // Computed Values
  // ─────────────────────────────────────────────────────────────────────────

  const isEditMode = !!productId;
  // Handle null family gracefully
  const enabledAxes = useMemo(() => family ? getEnabledVariantAxes(family) : [], [family]);
  const visualizationType: VisualizationType = family?.visualizationConfig?.requires3DModel
    ? 'model3d'
    : family?.visualizationConfig?.requiresSeamlessTexture
      ? 'surface'
      : 'image_only';

  // Check if form is dirty
  const isDirty = useMemo(() => {
    if (!product) return false;

    // Compare current state with original
    const currentClean = { ...product };
    delete (currentClean as any)._isDirty;
    delete (currentClean as any)._originalSnapshot;
    delete (currentClean as any)._validationErrors;

    const originalClean = originalProductRef.current
      ? { ...originalProductRef.current }
      : null;
    if (originalClean) {
      delete (originalClean as any)._isDirty;
      delete (originalClean as any)._originalSnapshot;
      delete (originalClean as any)._validationErrors;
    }

    const productChanged = JSON.stringify(currentClean) !== JSON.stringify(originalClean);
    const axisValuesChanged =
      JSON.stringify(axisValuesMap) !== JSON.stringify(originalAxisValuesRef.current);

    // Check if any variant has changed (SKU, commerce, visualization, etc.)
    const variantsChanged = variants.some((variant) => {
      // New variants are dirty only if they are selected (user restored a missing combination).
      // Deselected placeholder variants (_isNew + !isSelected) are just available-to-add slots.
      if (variant._isNew) return variant.isSelected;
      if (!variant._originalSnapshot) return true; // No original = dirty

      // Compare current variant state with its original snapshot using stable snapshot
      // Exclude thumbnailPath since UI may convert URL to base64 data URI
      // Strip cache-buster ?v= params from materialPath so display URLs don't cause false dirty
      const stripCacheBuster = (url: string | undefined) =>
        url ? url.replace(/[?&]v=\d+/g, '').replace(/\?$/, '') : url;
      const visualizationForSnapshot = variant.visualization ? {
        ...variant.visualization,
        thumbnailPath: undefined,
        surface: variant.visualization.surface ? {
          ...variant.visualization.surface,
          materialPath: stripCacheBuster(variant.visualization.surface.materialPath),
        } : variant.visualization.surface,
      } : variant.visualization;
      const currentSnapshot = createStableSnapshot({
        axes: variant.axes,
        sku: variant.sku,
        isSelected: variant.isSelected,
        isDefault: variant.isDefault,
        visualization: visualizationForSnapshot,
        galleryImages: variant.galleryImages ?? [],
        commerce: variant.commerce,
        siblingOrder: variant.siblingOrder,
      });
      // Pending file uploads always mark the variant dirty — user has
      // selected new gallery images that still need to be persisted.
      if ((variant._galleryUploads?.length ?? 0) > 0) return true;
      return currentSnapshot !== variant._originalSnapshot;
    });

    // Check if any original variant was deleted
    const currentVariantUuids = new Set(variants.map(v => v.uuid));
    const variantsDeleted = Array.from(originalVariantUuidsRef.current).some(
      uuid => !currentVariantUuids.has(uuid)
    );

    return productChanged || axisValuesChanged || variantsChanged || variantsDeleted;
  }, [product, axisValuesMap, variants]);

  // Group variants for table display
  const variantGroups = useMemo((): VariantGroupV3[] => {
    // Get axes that actually have real (non-default) values
    const axesWithValues = enabledAxes.filter(
      (axis) => axisValuesMap[axis.name]?.some(v => !isDefaultAxisValue(v))
    );

    if (variants.length === 0 || axesWithValues.length <= 1) {
      return [];
    }

    // Group by all axes except the last one
    const groupingAxes = axesWithValues.slice(0, -1);
    const expandAxis = axesWithValues[axesWithValues.length - 1];

    const groups = new Map<string, VariantGroupV3>();

    for (const variant of variants) {
      // Build group key from grouping axes
      const groupAxes: Record<string, AxisValue> = {};
      const keyParts: string[] = [];

      for (const axis of groupingAxes) {
        const value = variant.axes[axis.name];
        groupAxes[axis.name] = value;
        keyParts.push(`${axis.name}:${formatAxisValueForDisplay(value)}`);
      }

      const groupKey = keyParts.join('|');

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          groupKey,
          groupAxes,
          variants: [],
          expandAxis: expandAxis.name,
          isExpanded: true,
        });
      }

      groups.get(groupKey)!.variants.push(variant);
    }

    return Array.from(groups.values());
  }, [variants, enabledAxes, axisValuesMap]);

  // ─────────────────────────────────────────────────────────────────────────
  // Load Product (Edit Mode)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Process raw product data once family configuration is available.
   * Separated from fetching so the API call can run in parallel with family loading.
   */
  const processProductData = useCallback((mainProduct: ProductV2) => {
    // Combine the main product with its siblings to get all variants
    // siblings is now an array of full product objects (not UUIDs)
    const siblings = (mainProduct.siblings as unknown as ProductV2[]) || [];
    // Colour and pattern on the axes this family shows, whatever key or
    // spelling they were saved under (ARDE-293: products added by AI wrote
    // axes.color on 'label' families, and patterns only on the texture).
    const allProducts: ProductV2[] = [mainProduct, ...siblings].map((p) => ({
      ...p,
      axes: alignLoadedAxes(p.axes as Record<string, AxisValue>, enabledAxes, p.visualization as any) as any,
    }));

    // Sort by siblingOrder to restore user-defined variant order
    allProducts.sort((a, b) => (a.siblingOrder ?? 0) - (b.siblingOrder ?? 0));

    // Use the default variant or the fetched product as the main form state
    const defaultProduct = allProducts.find((p) => p.isDefault) || mainProduct;
    const formState = productToFormState(defaultProduct);

    // Extract axis values from all variants
    const extractedAxisValues = extractAxisValuesFromProducts(allProducts, enabledAxes);

    // Cache-bust suffix for image URLs — the worker may overwrite files at the same
    // CDN path, so a timestamp query param forces a fresh fetch on every load.
    const cacheSuffix = `v=${Date.now()}`;

    // Convert loaded products to VariantV3 format
    const loadedVariants: VariantV3[] = allProducts.map((p, index) => {
      // plankGroupId lives at visualization.surface.plankGroupId (canonical location)
      let visualization = p.visualization;

      // Normalize single-mode surface variants: clear inherited plank data (materialPaths,
      // plankGroupId) that doesn't belong on single-mode products.  This must happen BEFORE
      // the snapshot is created so the auto-detect effect in VariantMatrix doesn't produce
      // a diff that marks the variant as dirty on load.
      if (visualization?.surface?.isMultiple === false) {
        const { materialPaths, plankGroupId, ...restSurface } = visualization.surface;
        visualization = {
          ...visualization,
          surface: { ...restSurface, materialPaths: [], isMultiple: false },
        };
      }

      // Create a snapshot of ONLY the fields that are compared in categorizeVariants
      // Use createStableSnapshot for order-independent comparison
      // IMPORTANT: Exclude thumbnailPath as it gets converted to base64 by the UI for display
      // IMPORTANT: Strip ?v= cache busters from materialPath so snapshots use clean URLs
      const stripV = (url: string | undefined) =>
        url ? url.replace(/[?&]v=\d+/g, '').replace(/\?$/, '') : url;
      const visualizationForSnapshot = visualization ? {
        ...visualization,
        thumbnailPath: undefined, // Exclude from comparison
        surface: visualization.surface ? {
          ...visualization.surface,
          materialPath: stripV(visualization.surface.materialPath),
        } : visualization.surface,
      } : visualization;

      const snapshotData = {
        axes: p.axes,
        sku: p.sku,
        isSelected: true,
        isDefault: p.isDefault,
        visualization: visualizationForSnapshot,
        galleryImages: (p as any).galleryImages ?? [],
        commerce: p.commerce,
        siblingOrder: p.siblingOrder ?? index,
      };

      // Apply cache buster to display URLs AFTER snapshot creation (snapshot uses clean URLs)
      const appendCacheSuffix = (url: string | undefined) => {
        if (!url || url.startsWith('data:')) return url;
        // Strip any existing ?v= or &v= params to avoid stacking
        const cleanUrl = url.replace(/[?&]v=\d+/g, '').replace(/\?$/, '');
        return `${cleanUrl}${cleanUrl.includes('?') ? '&' : '?'}${cacheSuffix}`;
      };
      const displayVisualization = visualization ? {
        ...visualization,
        thumbnailPath: appendCacheSuffix(visualization.thumbnailPath),
        surface: visualization.surface ? {
          ...visualization.surface,
          materialPath: appendCacheSuffix(visualization.surface.materialPath),
        } : visualization.surface,
      } : visualization;

      return {
        uuid: p.uuid,
        // Drafts created by an API integration sync have no axes yet, so
        // fall back to an empty object. Downstream code uses `Object.entries`
        // on this value and crashes on null/undefined.
        axes: (p.axes ?? {}) as Record<string, AxisValue>,
        sku: p.sku,
        isSelected: true,
        isDefault: p.isDefault,
        visualization: displayVisualization,
        galleryImages: [...((p as any).galleryImages ?? [])],
        commerce: p.commerce ?? { link: '', addToCartLink: '' },
        // Read-only: what the API resolved for shoppers (price & packaging card).
        ...(p.pricing ? { pricing: p.pricing } : {}),
        siblingOrder: p.siblingOrder ?? index,
        _isNew: false,
        _originalSnapshot: createStableSnapshot(snapshotData),
      };
    });

    // Generate the full cartesian product and add missing combinations as
    // deselected (isSelected: false) placeholders so users can restore them.
    // This restores the old UX where all possible combinations were visible.
    const groupingAxes = enabledAxes.filter((axis) => axis.isGroupingAxis);
    const axisArrays: AxisValue[][] = enabledAxes.map(
      (axis) => (extractedAxisValues[axis.name]?.length > 0) ? extractedAxisValues[axis.name] : [AXIS_DEFAULT_VALUE]
    );
    const allCombinations = cartesianProduct(axisArrays);

    // Build a set of existing variant keys for fast lookup
    const existingKeys = new Set(
      loadedVariants.map((v) => generateVariantKeyFromAxes(v.axes))
    );

    // Pre-populate surface cache from loaded variants for plankGroupId + surface data inheritance
    const plankGroupIdCache = new Map<string, string>();
    const groupSurfaceCache = new Map<string, { materialPaths?: string[]; grout?: any; isMultiple?: boolean }>();
    for (const variant of loadedVariants) {
      const surface = variant.visualization?.surface;
      const gKey = groupingAxes.map(a => `${a.name}:${formatAxisValueForDisplay(variant.axes[a.name])}`).join('|');
      if (surface?.plankGroupId && !plankGroupIdCache.has(gKey)) {
        plankGroupIdCache.set(gKey, surface.plankGroupId);
      }
      if (surface?.materialPaths?.length && !groupSurfaceCache.has(gKey)) {
        groupSurfaceCache.set(gKey, {
          materialPaths: surface.materialPaths,
          grout: surface.grout,
          isMultiple: surface.isMultiple ?? true,
        });
      }
    }

    // Create deselected placeholder variants for missing combinations
    const missingVariants: VariantV3[] = [];
    for (const combo of allCombinations) {
      const axes: Record<string, AxisValue> = {};
      enabledAxes.forEach((axis, i) => {
        axes[axis.name] = combo[i];
      });
      const key = generateVariantKeyFromAxes(axes);
      if (existingKeys.has(key)) continue;

      const groupKey = groupingAxes.map(a => `${a.name}:${formatAxisValueForDisplay(axes[a.name])}`).join('|');
      if (!plankGroupIdCache.has(groupKey)) {
        plankGroupIdCache.set(groupKey, generatePlankGroupId(axes, groupingAxes));
      }
      const plankGroupId = plankGroupIdCache.get(groupKey)!;
      const defaultVisualization = createDefaultVisualization(visualizationType);
      const existingGroupSurface = groupSurfaceCache.get(groupKey);

      missingVariants.push({
        uuid: uuidv4(),
        axes,
        sku: '',
        isSelected: false,
        isDefault: false,
        visualization: visualizationType === 'surface'
          ? {
              ...defaultVisualization,
              surface: {
                ...defaultVisualization.surface,
                plankGroupId,
                ...(existingGroupSurface && {
                  materialPaths: existingGroupSurface.materialPaths,
                  grout: existingGroupSurface.grout,
                  isMultiple: existingGroupSurface.isMultiple,
                }),
              },
            }
          : defaultVisualization,
        // Links, price, packaging and sample from the product, so a restored combination is not left unpriced.
        commerce: inheritedCommerce(formState.commerce),
        _isNew: true,
      });
    }

    const allVariants = [...loadedVariants, ...missingVariants];

    setProduct(formState);
    setAxisValuesMap(extractedAxisValues);
    setVariants(allVariants);

    // Skip the next variant regeneration since we just loaded variants with correct data
    skipNextRegenerationRef.current = true;

    // Store originals for reset
    originalProductRef.current = { ...formState };
    originalAxisValuesRef.current = { ...extractedAxisValues };
    prevAxisValuesRef.current = { ...extractedAxisValues };
    // Store original variant UUIDs to detect deletions
    originalVariantUuidsRef.current = new Set(loadedVariants.map(v => v.uuid));

    // If the product is a "draft template" (single loaded variant with no
    // axes yet), stash it so regeneration can reuse its uuid + snapshot
    // even across intermediate wipes (e.g. while the user is still entering
    // axis values one at a time and required axes aren't all set).
    const realLoaded = loadedVariants.filter(v => !v._isNew);
    if (
      realLoaded.length === 1 &&
      Object.keys(realLoaded[0].axes ?? {}).length === 0
    ) {
      draftTemplateRef.current = realLoaded[0];
    } else {
      draftTemplateRef.current = null;
    }

    setIsLoading(false);
  }, [enabledAxes, visualizationType]);

  // Fetch raw product data immediately on mount (runs in parallel with family loading)
  useEffect(() => {
    if (!productId || productFetchedRef.current) return;
    productFetchedRef.current = true;

    setIsLoading(true);
    setError(null);

    productV2Api.getProduct(productId).then((mainProduct) => {
      if (!mainProduct) {
        throw new Error('Product not found');
      }
      rawProductCacheRef.current = mainProduct;
      setRawProductReady(true); // Trigger family effect to process the data
    }).catch((err: any) => {
      setError(err.message || 'Failed to load product');
      console.error('[useProductFormV3] Error loading product:', err);
      setIsLoading(false);
    });
  }, [productId]);

  // Once family becomes available, process cached product data or initialize empty form
  useEffect(() => {
    if (!family) return;

    if (productId) {
      // Edit mode: process cached product data if available
      if (rawProductCacheRef.current) {
        processProductData(rawProductCacheRef.current);
      }
      // If not yet fetched, this effect will re-run when rawProductReady becomes true
    } else {
      // Create mode: initialize empty form
      const emptyState = createEmptyFormState(vendorId, family._id, family.slug);
      emptyState.visualization = createDefaultVisualization(visualizationType);
      emptyState.brand = vendorName || '';

      // Apply pre-fill data from external sources (e.g., product page scraping)
      if (initialData) {
        if (initialData.name) emptyState.name = initialData.name;
        if (initialData.brand) emptyState.brand = initialData.brand;
        if (initialData.description) emptyState.description = initialData.description;
        if (initialData.sku) {
          emptyState.sku = initialData.sku;
          /*
           * And on the variant, which is the one the form actually requires.
           *
           * SKU lives on the variant in the V2 model; the product-level field
           * is a default that nothing reads back. Setting only that left the
           * vendor looking at a scraped product with a red, empty, required
           * SKU box and the reference sitting on the page it came from.
           *
           * A single seed, consumed by the first variant minted. Generation
           * runs again on every axis change, and re-stamping the same code
           * onto each new combination would hand several variants one SKU.
           */
          seedSkuRef.current = initialData.sku;
        }
        if (initialData.productPageUrl) emptyState.commerce = { ...emptyState.commerce, link: initialData.productPageUrl };
        if (initialData.productType) emptyState.organization = { ...emptyState.organization, productType: initialData.productType.toLowerCase() };
        if (initialData.applicationTypes?.length) emptyState.organization = { ...emptyState.organization, applicationTypes: initialData.applicationTypes };

        // Pre-fill axis values (drives variant generation + axis editor UI)
        const initialAxisValues: AxisValuesMap = {};
        if (initialData.color) {
          // The family's own colour axis: 'label', 'color' or 'color-f'
          // depending on when the family was created (ARDE-293). The scraper
          // may answer a plain name as well as { value, hex }.
          const familyAxes = family ? getEnabledVariantAxes(family) : [];
          const colorAxisName = colorAxisNameOf(familyAxes) || 'color';
          const colorValue = colorValueForAxis(colorAxisName, initialData.color);
          if (colorValue !== undefined) initialAxisValues[colorAxisName] = [colorValue];
        }
        if (initialData.size) {
          initialAxisValues['size'] = [{
            width: initialData.size.width,
            height: initialData.size.height,
            depth: initialData.size.depth || undefined,
            unit: initialData.size.unit || 'cm',
          }];
        }
        // The pattern grid's own name for it ('Brick bond' → 'bricks'): a
        // spelling the grid does not know was selected on no tile, so the
        // product saved with a pattern nobody could see (ARDE-293). Stack bond
        // for a surface when the page named none, or none the generator lays.
        const pattern = knownPatternName(initialData.pattern);
        if (pattern) {
          initialAxisValues['pattern'] = [pattern];
        } else if (visualizationType === 'surface') {
          initialAxisValues['pattern'] = ['stackbond'];
        }
        if (Object.keys(initialAxisValues).length > 0) {
          setAxisValuesMap(initialAxisValues);
        }
      }

      setProduct(emptyState);
      originalProductRef.current = { ...emptyState };
      setIsLoading(false);
    }
  }, [family, productId, vendorId, visualizationType, processProductData, rawProductReady, initialData]);

  // ─────────────────────────────────────────────────────────────────────────
  // Field Setters
  // ─────────────────────────────────────────────────────────────────────────

  const setField = useCallback(<T>(path: FieldPath, value: T) => {
    setProduct((prev) => {
      if (!prev) return prev;
      const updated = setNestedValue(prev, path, value);
      return { ...updated, _isDirty: true };
    });
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Axis Management
  // ─────────────────────────────────────────────────────────────────────────

  const setAxisValues = useCallback((axisName: string, values: AxisValue[]) => {
    setAxisValuesMap((prev) => {
      const updated = {
        ...prev,
        [axisName]: values,
      };
      return updated;
    });
  }, []);

  // Auto-regenerate variants when axis values change
  useEffect(() => {
    // Skip regeneration if variants were just loaded from API
    // This prevents overwriting loaded variants (with correct _originalSnapshot) with new objects
    if (skipNextRegenerationRef.current) {
      skipNextRegenerationRef.current = false;
      return;
    }

    if (!product || Object.keys(axisValuesMap).length === 0) {
      return;
    }

    // Check if all REQUIRED axes have at least one value
    // Optional axes with no values use AXIS_DEFAULT_VALUE as placeholder
    const requiredAxesHaveValues = enabledAxes.every(
      (axis) => !axis.required || axisValuesMap[axis.name]?.length > 0
    );

    if (!requiredAxesHaveValues) {
      // Don't generate new cartesian variants until all required axes have
      // values. But we MUST preserve every variant whose uuid is in the
      // original-variant-uuids set — those are real mongo-backed docs
      // (including partially-filled drafts that have already been saved
      // once). Wiping them from state would make categorizeVariants see
      // them as "deleted" on the next save and drop the document.
      //
      // Project any partial axis values the user has entered onto each
      // preserved variant so a Save captures them. User-level edits on the
      // live variants (sku, visualization, gallery, etc.) are preserved
      // because we read from `variantsRef.current`, not from the loaded
      // snapshot.
      const partialAxes: Record<string, AxisValue> = {};
      for (const axis of enabledAxes) {
        const values = axisValuesMap[axis.name];
        if (values && values.length > 0) {
          partialAxes[axis.name] = values[0];
        }
      }

      const preservedFromState = variantsRef.current.filter((v) =>
        originalVariantUuidsRef.current.has(v.uuid),
      );

      if (preservedFromState.length > 0) {
        // Keep every real variant, just update its axes to the current
        // partial values. This handles both fresh drafts (single empty
        // variant) and partially-saved drafts (single or multiple).
        setVariants(
          preservedFromState.map((v) => ({ ...v, axes: { ...partialAxes } })),
        );
      } else if (draftTemplateRef.current) {
        // Fallback: no variants survive in state but we still have the
        // initial draft template ref — rehydrate from it so an immediate
        // Save doesn't delete the backing document.
        setVariants([{ ...draftTemplateRef.current, axes: partialAxes }]);
      } else {
        // Create mode (no original variants): safe to clear.
        setVariants([]);
      }

      // Update previous axis values so re-adding the same value is detected as "new"
      prevAxisValuesRef.current = { ...axisValuesMap };
      return;
    }

    // Get grouping axes for plankGroupId computation
    const groupingAxes = enabledAxes.filter((axis) => axis.isGroupingAxis);

    // Build arrays for cartesian product (using all enabled axes)
    // Empty optional axes get [AXIS_DEFAULT_VALUE] so every variant's axes record includes all enabled axes
    const axisArrays: AxisValue[][] = enabledAxes.map(
      (axis) => (axisValuesMap[axis.name]?.length > 0) ? axisValuesMap[axis.name] : [AXIS_DEFAULT_VALUE]
    );

    // Generate all combinations
    const combinations = cartesianProduct(axisArrays);

    // Map existing variants by their axes key for reuse.
    // IMPORTANT: Use variantsRef.current to get the LATEST variants, not stale closure.
    // Include deselected variants too — when their combo is still in the
    // cartesian product they need to be reused (preserving isSelected: false)
    // instead of being skipped, otherwise session-deselected variants vanish
    // on the next regen.
    // Empty-axes variants (loaded draft templates OR variants adopted from a
    // draft on a previous regen) are kept in the map under the '' key so
    // subsequent regens that produce an all-null combo find them and reuse
    // the existing uuid + snapshot rather than minting a new one.
    const existingVariantsByKey = new Map<string, VariantV3>();
    for (const variant of variantsRef.current) {
      const key = generateVariantKeyFromAxes(variant.axes);
      // Selected variants take precedence over deselected ones if both
      // somehow share a key (defensive — shouldn't happen in practice).
      if (variant.isSelected || !existingVariantsByKey.has(key)) {
        existingVariantsByKey.set(key, variant);
      }
    }
    // Draft template carried across regenerations via the persistent ref.
    // It's the mongo-backed variant of a draft product with no axes yet and
    // is used ONLY when existingVariantsByKey doesn't already cover reuse
    // (e.g. after `setVariants([])` has wiped the local state between renders).
    let draftTemplate: VariantV3 | null = draftTemplateRef.current;

    // Track plankGroupIds and surface data to reuse for variants with same grouping axis values
    const plankGroupIdCache = new Map<string, string>();
    const groupSurfaceCache = new Map<string, { materialPaths?: string[]; grout?: any; isMultiple?: boolean }>();

    // Pre-populate surface cache from existing variants so new variants in the
    // same group (e.g. same color+size) inherit materialPaths, grout, and isMultiple.
    for (const variant of variantsRef.current) {
      if (!variant.isSelected) continue;
      const surface = variant.visualization?.surface;
      if (!surface?.materialPaths?.length) continue;
      const gKey = groupingAxes.map(a => `${a.name}:${formatAxisValueForDisplay(variant.axes[a.name])}`).join('|');
      if (!groupSurfaceCache.has(gKey)) {
        groupSurfaceCache.set(gKey, {
          materialPaths: surface.materialPaths,
          grout: surface.grout,
          isMultiple: surface.isMultiple ?? true,
        });
      }
    }

    // Determine which axis values are new (added since last regeneration).
    // Only combinations involving at least one new value will create new variants.
    // This prevents filling in missing combinations when the user has a curated subset.
    const prevAxisValues = prevAxisValuesRef.current;
    const newAxisValueSets = new Map<string, Set<string>>();
    enabledAxes.forEach((axis) => {
      const prevDisplayValues = new Set(
        (prevAxisValues[axis.name] || []).map((v: AxisValue) => formatAxisValueForDisplay(v))
      );
      const newForAxis = new Set<string>();
      for (const val of (axisValuesMap[axis.name] || [])) {
        const display = formatAxisValueForDisplay(val);
        if (!prevDisplayValues.has(display)) {
          newForAxis.add(display);
        }
      }
      newAxisValueSets.set(axis.name, newForAxis);
    });
    const hasAnyPreviousValues = Object.values(prevAxisValues).some(
      (vals: AxisValue[]) => vals && vals.length > 0
    );

    // Create variants from combinations — only for existing or truly new combos
    const newVariants: VariantV3[] = [];
    for (let index = 0; index < combinations.length; index++) {
      const combo = combinations[index];
      // Build axes object for this combination (using all enabled axes)
      const axes: Record<string, AxisValue> = {};
      enabledAxes.forEach((axis, i) => {
        axes[axis.name] = combo[i];
      });

      const key = generateVariantKeyFromAxes(axes);
      const existing = existingVariantsByKey.get(key);

      if (existing) {
        // Reuse existing variant data but update plankGroupId if needed
        const plankGroupId = generatePlankGroupId(axes, groupingAxes);

        // Check cache for consistent plankGroupIds.
        // Only inherit plankGroupId from multiple-mode variants (those with materialPaths).
        // Single-mode variants (materialPath but no materialPaths) don't share planks.
        const groupKey = groupingAxes.map(a => `${a.name}:${formatAxisValueForDisplay(axes[a.name])}`).join('|');
        if (visualizationType === 'surface') {
          const isMultipleMode = (existing.visualization?.surface?.materialPaths?.length || 0) > 0;
          if (!plankGroupIdCache.has(groupKey) && isMultipleMode) {
            plankGroupIdCache.set(groupKey, existing.visualization?.surface?.plankGroupId || plankGroupId);
          }
        }

        newVariants.push({
          ...existing,
          axes,
          visualization: visualizationType === 'surface'
            ? {
                ...existing.visualization,
                surface: {
                  ...existing.visualization?.surface,
                  plankGroupId: plankGroupIdCache.get(groupKey) ?? existing.visualization?.surface?.plankGroupId ?? plankGroupId,
                },
              }
            : existing.visualization,
        });
        continue;
      }

      // Only create a new variant if at least one axis value in this combination is new,
      // or if this is the first generation (no previous axis values).
      const hasNewValue = !hasAnyPreviousValues || enabledAxes.some((axis, i) => {
        const display = formatAxisValueForDisplay(combo[i]);
        return newAxisValueSets.get(axis.name)!.has(display);
      });

      if (!hasNewValue) continue;

      // Generate plankGroupId for new variants
      const groupKey = groupingAxes.map(a => `${a.name}:${formatAxisValueForDisplay(axes[a.name])}`).join('|');
      if (!plankGroupIdCache.has(groupKey)) {
        plankGroupIdCache.set(groupKey, generatePlankGroupId(axes, groupingAxes));
      }
      const plankGroupId = plankGroupIdCache.get(groupKey)!;

      // Create new variant with plankGroupId.
      // If existing variants in the same group already have planks, inherit
      // materialPaths and grout so the UI auto-detects "multiple" upload mode.
      const defaultVisualization = createDefaultVisualization(visualizationType);
      const existingGroupSurface = groupSurfaceCache.get(groupKey);

      // If we're generating variants for a draft product that was loaded
      // with no axes, donate the draft template's uuid + visualization +
      // commerce + snapshot to the first generated variant so the submission
      // layer modifies the existing mongo doc instead of deleting it and
      // creating a fresh one (which would break product identity).
      //
      // IMPORTANT: prefer the live copy of the template in variantsRef (the
      // partial-branch clone) over the stale `draftTemplateRef.current`. The
      // live clone carries any edits the vendor made before completing all
      // required axes (sku overrides, visualization uploads, gallery, etc.).
      // Falling back to the ref would silently wipe those edits as soon as
      // the first real combo is generated.
      const adoptDraftTemplate = draftTemplate !== null;
      const liveTemplate = adoptDraftTemplate
        ? variantsRef.current.find((v) => v.uuid === draftTemplate!.uuid) ?? null
        : null;
      const donor = liveTemplate ?? draftTemplate;
      if (adoptDraftTemplate) {
        draftTemplate = null; // Only the first combo inherits (this call)
        draftTemplateRef.current = null; // And never again across regenerations
      }

      newVariants.push({
        uuid: adoptDraftTemplate ? donor!.uuid : uuidv4(),
        axes,
        sku: adoptDraftTemplate ? (donor!.sku ?? '') : takeSeedSku(),
        isSelected: true,
        isDefault: adoptDraftTemplate ? donor!.isDefault : false,
        visualization: adoptDraftTemplate
          ? donor!.visualization
          : visualizationType === 'surface'
            ? {
                ...defaultVisualization,
                surface: {
                  ...defaultVisualization.surface,
                  plankGroupId,
                  ...(existingGroupSurface && {
                    materialPaths: existingGroupSurface.materialPaths,
                    grout: existingGroupSurface.grout,
                    isMultiple: existingGroupSurface.isMultiple,
                  }),
                },
              }
            : defaultVisualization,
        galleryImages: adoptDraftTemplate ? donor!.galleryImages : undefined,
        commerce: adoptDraftTemplate
          ? donor!.commerce
          : inheritedCommerce(product.commerce),
        // Preserve the _originalSnapshot so categorizeVariants classifies this
        // as "modified" (mongo doc exists) rather than "new".
        _isNew: !adoptDraftTemplate,
        _originalSnapshot: adoptDraftTemplate ? donor!._originalSnapshot : undefined,
      });
    }

    // Preserve soft-deleted variants (isSelected: false) that are not in the new
    // cartesian product. Two cases to keep:
    //   - Loaded variants the user chose to delete (e.g. by removing an axis
    //     value): kept so the submission hook can send delete operations.
    //   - In-session (`_isNew: true`) variants the user deselected as
    //     "not-available" placeholders: kept so the UI can keep showing the
    //     transparent-red row + Restore button across regens.
    const newVariantUuids = new Set(newVariants.map((v) => v.uuid));
    const softDeletedToKeep = variantsRef.current.filter(
      (v) => !v.isSelected && !newVariantUuids.has(v.uuid)
    );

    const allVariants = [...newVariants, ...softDeletedToKeep];

    // Ensure exactly one default variant among selected
    const defaultVariants = allVariants.filter((v) => v.isSelected && v.isDefault);
    if (defaultVariants.length > 1) {
      // Multiple defaults — keep only the first, clear the rest
      for (let i = 1; i < defaultVariants.length; i++) {
        defaultVariants[i].isDefault = false;
      }
    } else if (defaultVariants.length === 0) {
      // No default — set the first selected variant as default
      const firstSelected = allVariants.find((v) => v.isSelected);
      if (firstSelected) {
        firstSelected.isDefault = true;
      }
    }

    setVariants(allVariants);

    // Update previous axis values for next incremental comparison
    prevAxisValuesRef.current = { ...axisValuesMap };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axisValuesMap, product, enabledAxes, visualizationType]);

  const getAxisValues = useCallback(
    (axisName: string): AxisValue[] => {
      return axisValuesMap[axisName] || [];
    },
    [axisValuesMap]
  );

  const getAllAxisValues = useCallback((): AxisValuesMap => {
    return axisValuesMap;
  }, [axisValuesMap]);

  // ─────────────────────────────────────────────────────────────────────────
  // Variant Generation
  // ─────────────────────────────────────────────────────────────────────────

  const regenerateVariants = useCallback(() => {
    if (!product) return;

    // Check if all REQUIRED axes have at least one value
    // Optional axes with no values use AXIS_DEFAULT_VALUE as placeholder
    const requiredAxesHaveValues = enabledAxes.every(
      (axis) => !axis.required || axisValuesMap[axis.name]?.length > 0
    );

    if (!requiredAxesHaveValues) {
      // Don't generate new cartesian variants until all required axes have
      // values. But we MUST preserve every variant whose uuid is in the
      // original-variant-uuids set — those are real mongo-backed docs
      // (including partially-filled drafts that have already been saved
      // once). Wiping them from state would make categorizeVariants see
      // them as "deleted" on the next save and drop the document.
      //
      // Project any partial axis values the user has entered onto each
      // preserved variant so a Save captures them. User-level edits on the
      // live variants (sku, visualization, gallery, etc.) are preserved
      // because we read from `variantsRef.current`, not from the loaded
      // snapshot.
      const partialAxes: Record<string, AxisValue> = {};
      for (const axis of enabledAxes) {
        const values = axisValuesMap[axis.name];
        if (values && values.length > 0) {
          partialAxes[axis.name] = values[0];
        }
      }

      const preservedFromState = variantsRef.current.filter((v) =>
        originalVariantUuidsRef.current.has(v.uuid),
      );

      if (preservedFromState.length > 0) {
        // Keep every real variant, just update its axes to the current
        // partial values. This handles both fresh drafts (single empty
        // variant) and partially-saved drafts (single or multiple).
        setVariants(
          preservedFromState.map((v) => ({ ...v, axes: { ...partialAxes } })),
        );
      } else if (draftTemplateRef.current) {
        // Fallback: no variants survive in state but we still have the
        // initial draft template ref — rehydrate from it so an immediate
        // Save doesn't delete the backing document.
        setVariants([{ ...draftTemplateRef.current, axes: partialAxes }]);
      } else {
        // Create mode (no original variants): safe to clear.
        setVariants([]);
      }

      // Update previous axis values so re-adding the same value is detected as "new"
      prevAxisValuesRef.current = { ...axisValuesMap };
      return;
    }

    // Get grouping axes for plankGroupId computation
    const groupingAxes = enabledAxes.filter((axis) => axis.isGroupingAxis);

    // Build arrays for cartesian product (using all enabled axes)
    // Empty optional axes get [AXIS_DEFAULT_VALUE] so every variant's axes record includes all enabled axes
    const axisArrays: AxisValue[][] = enabledAxes.map(
      (axis) => (axisValuesMap[axis.name]?.length > 0) ? axisValuesMap[axis.name] : [AXIS_DEFAULT_VALUE]
    );

    // Generate all combinations
    const combinations = cartesianProduct(axisArrays);

    // Map existing variants by their axes key for reuse
    const existingVariantsByKey = new Map<string, VariantV3>();
    for (const variant of variants) {
      const key = generateVariantKeyFromAxes(variant.axes);
      existingVariantsByKey.set(key, variant);
    }
    // Draft template carried across regenerations via the persistent ref.
    // Used when the combo key doesn't match anything in the existing map so
    // the first generated variant adopts the mongo-backed draft's uuid +
    // sku + visualization + snapshot, instead of being minted as a fresh
    // variant (which would look like "delete old + create new" to the
    // submission layer and wipe the SKU).
    let draftTemplate: VariantV3 | null = draftTemplateRef.current;

    // Track plankGroupIds and surface data to reuse for variants with same grouping axis values
    const plankGroupIdCache = new Map<string, string>();
    const groupSurfaceCache = new Map<string, { materialPaths?: string[]; grout?: any; isMultiple?: boolean }>();

    // Pre-populate surface cache from existing variants so new variants in the
    // same group (e.g. same color+size) inherit materialPaths, grout, and isMultiple.
    for (const variant of variants) {
      if (!variant.isSelected) continue;
      const surface = variant.visualization?.surface;
      if (!surface?.materialPaths?.length) continue;
      const gKey = groupingAxes.map(a => `${a.name}:${formatAxisValueForDisplay(variant.axes[a.name])}`).join('|');
      if (!groupSurfaceCache.has(gKey)) {
        groupSurfaceCache.set(gKey, {
          materialPaths: surface.materialPaths,
          grout: surface.grout,
          isMultiple: surface.isMultiple ?? true,
        });
      }
    }

    // Create variants from combinations
    const newVariants: VariantV3[] = combinations.map((combo, index) => {
      // Build axes object for this combination (using all enabled axes)
      const axes: Record<string, AxisValue> = {};
      enabledAxes.forEach((axis, i) => {
        axes[axis.name] = combo[i];
      });

      const key = generateVariantKeyFromAxes(axes);
      const existing = existingVariantsByKey.get(key);

      if (existing) {
        // Reuse existing variant data but update plankGroupId if needed
        const plankGroupId = generatePlankGroupId(axes, groupingAxes);

        // Check cache for consistent plankGroupIds.
        // Only inherit plankGroupId from multiple-mode variants (those with materialPaths).
        // Single-mode variants (materialPath but no materialPaths) don't share planks.
        const groupKey = groupingAxes.map(a => `${a.name}:${formatAxisValueForDisplay(axes[a.name])}`).join('|');
        if (visualizationType === 'surface') {
          const isMultipleMode = (existing.visualization?.surface?.materialPaths?.length || 0) > 0;
          if (!plankGroupIdCache.has(groupKey) && isMultipleMode) {
            plankGroupIdCache.set(groupKey, existing.visualization?.surface?.plankGroupId || plankGroupId);
          }
        }

        return {
          ...existing,
          axes,
          visualization: visualizationType === 'surface'
            ? {
                ...existing.visualization,
                surface: {
                  ...existing.visualization?.surface,
                  plankGroupId: plankGroupIdCache.get(groupKey) ?? existing.visualization?.surface?.plankGroupId ?? plankGroupId,
                },
              }
            : existing.visualization,
        };
      }

      // Generate plankGroupId for new variants
      const groupKey = groupingAxes.map(a => `${a.name}:${formatAxisValueForDisplay(axes[a.name])}`).join('|');
      if (!plankGroupIdCache.has(groupKey)) {
        plankGroupIdCache.set(groupKey, generatePlankGroupId(axes, groupingAxes));
      }
      const plankGroupId = plankGroupIdCache.get(groupKey)!;

      // Create new variant with plankGroupId.
      // If existing variants in the same group already have planks, inherit
      // materialPaths, grout, and isMultiple so the UI auto-detects "multiple" upload mode
      // and the submission hook correctly triggers seamless generation.
      //
      // Draft-product path: if we're editing a draft whose backing mongo doc
      // exists but had no axes yet, the FIRST new combo adopts the template's
      // uuid + sku + visualization + snapshot. Prefer the live copy of the
      // template in the current `variants` array (user edits win over the
      // initially-loaded snapshot in the ref).
      const defaultVisualization = createDefaultVisualization(visualizationType);
      const existingGroupSurface = groupSurfaceCache.get(groupKey);

      const adoptDraftTemplate = draftTemplate !== null;
      const liveTemplate = adoptDraftTemplate
        ? variants.find((v) => v.uuid === draftTemplate!.uuid) ?? null
        : null;
      const donor = liveTemplate ?? draftTemplate;
      if (adoptDraftTemplate) {
        draftTemplate = null; // Only the first combo inherits (this call)
        draftTemplateRef.current = null; // And never again across regenerations
      }

      return {
        uuid: adoptDraftTemplate ? donor!.uuid : uuidv4(),
        axes,
        sku: adoptDraftTemplate ? (donor!.sku ?? '') : takeSeedSku(),
        isSelected: true,
        isDefault: adoptDraftTemplate ? !!donor!.isDefault : false,
        visualization: adoptDraftTemplate
          ? donor!.visualization
          : visualizationType === 'surface'
            ? {
                ...defaultVisualization,
                surface: {
                  ...defaultVisualization.surface,
                  plankGroupId,
                  ...(existingGroupSurface && {
                    materialPaths: existingGroupSurface.materialPaths,
                    grout: existingGroupSurface.grout,
                    isMultiple: existingGroupSurface.isMultiple,
                  }),
                },
              }
            : defaultVisualization,
        galleryImages: adoptDraftTemplate ? donor!.galleryImages : undefined,
        commerce: adoptDraftTemplate
          ? donor!.commerce
          : inheritedCommerce(product.commerce),
        // Adopted variants carry the loaded snapshot so categorizeVariants
        // classifies them as "modified" rather than "new" (which is what
        // actually saves them instead of creating a duplicate doc).
        _isNew: !adoptDraftTemplate,
        _originalSnapshot: adoptDraftTemplate ? donor!._originalSnapshot : undefined,
      };
    });

    // Preserve soft-deleted variants (isSelected: false) that are not in the new
    // cartesian product. Includes both loaded variants (so the submission hook
    // can send delete ops) and in-session `_isNew` placeholders the user
    // explicitly deselected (so the UI keeps showing the Restore row).
    const newVariantUuids = new Set(newVariants.map((v) => v.uuid));
    const softDeletedToKeep = variants.filter(
      (v) => !v.isSelected && !newVariantUuids.has(v.uuid)
    );

    const allVariants = [...newVariants, ...softDeletedToKeep];

    // Ensure exactly one default variant among selected
    const defaultVariants = allVariants.filter((v) => v.isSelected && v.isDefault);
    if (defaultVariants.length > 1) {
      // Multiple defaults — keep only the first, clear the rest
      for (let i = 1; i < defaultVariants.length; i++) {
        defaultVariants[i].isDefault = false;
      }
    } else if (defaultVariants.length === 0) {
      // No default — set the first selected variant as default
      const firstSelected = allVariants.find((v) => v.isSelected);
      if (firstSelected) {
        firstSelected.isDefault = true;
      }
    }

    setVariants(allVariants);
  }, [product, enabledAxes, axisValuesMap, variants, visualizationType]);

  // ─────────────────────────────────────────────────────────────────────────
  // Variant Operations
  // ─────────────────────────────────────────────────────────────────────────

  const assignAxisValueToVariants = useCallback((axisName: string, value: AxisValue) => {
    setVariants((prev) =>
      prev.map((v) => {
        if (!isDefaultAxisValue(v.axes[axisName])) return v;
        return { ...v, axes: { ...v.axes, [axisName]: value } };
      })
    );
  }, []);

  const setVariantField = useCallback((uuid: string, field: string, value: unknown) => {
    setVariants((prev) =>
      prev.map((v) => {
        if (v.uuid !== uuid) return v;
        return { ...v, [field]: value, _isDirty: true };
      })
    );
  }, []);

  const deleteVariant = useCallback((uuid: string) => {
    // Mark variant as not selected (soft delete) instead of removing from array
    // This keeps the row visible in the UI with "deleted" styling
    // The actual deletion happens on save when isSelected: false variants are sent as delete operations
    setVariants((prev) =>
      prev.map((v) => {
        if (v.uuid !== uuid) return v;
        return { ...v, isSelected: false };
      })
    );
  }, []);

  const setDefaultVariant = useCallback((uuid: string) => {
    setVariants((prev) =>
      prev.map((v) => ({
        ...v,
        isDefault: v.uuid === uuid,
      }))
    );
  }, []);

  const selectVariant = useCallback((uuid: string, selected: boolean) => {
    setVariants((prev) =>
      prev.map((v) => {
        if (v.uuid !== uuid) return v;
        return { ...v, isSelected: selected };
      })
    );
  }, []);

  const selectAllVariants = useCallback((selected: boolean) => {
    setVariants((prev) =>
      prev.map((v) => ({
        ...v,
        isSelected: selected,
      }))
    );
  }, []);

  const reorderVariants = useCallback((activeId: string, overId: string) => {
    setVariants(prev => {
      const oldIndex = prev.findIndex(v => v.uuid === activeId);
      const newIndex = prev.findIndex(v => v.uuid === overId);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const reordered = arrayMove(prev, oldIndex, newIndex);
      // Assign siblingOrder based on new array position
      return reordered.map((v, i) => ({ ...v, siblingOrder: i }));
    });
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Visualization Operations
  // ─────────────────────────────────────────────────────────────────────────

  const setVisualizationField = useCallback((uuid: string, field: string, value: unknown) => {
    setVariants((prev) =>
      prev.map((v) => {
        if (v.uuid !== uuid) return v;
        const updatedVisualization = setNestedValue(v.visualization, field, value);
        return { ...v, visualization: updatedVisualization, _isDirty: true };
      })
    );
  }, []);

  /**
   * Patches arbitrary top-level variant fields. Used for non-axis, non-
   * visualization state like gallery images (existing URLs + pending uploads).
   */
  const patchVariant = useCallback((uuid: string, patch: Partial<VariantV3>) => {
    setVariants((prev) =>
      prev.map((v) => (v.uuid !== uuid ? v : { ...v, ...patch, _isDirty: true }))
    );
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Form Actions
  // ─────────────────────────────────────────────────────────────────────────

  const reset = useCallback(() => {
    if (originalProductRef.current) {
      setProduct({ ...originalProductRef.current });
    }
    if (originalAxisValuesRef.current) {
      setAxisValuesMap({ ...originalAxisValuesRef.current });
    }
    // Reload variants from cached product data
    if (rawProductCacheRef.current) {
      processProductData(rawProductCacheRef.current);
    }
  }, [processProductData]);

  const reload = useCallback(async (silent?: boolean, failedVariants?: VariantV3[]) => {
    if (!productId) return;
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const mainProduct = await productV2Api.getProduct(productId);
      if (!mainProduct) throw new Error('Product not found');
      rawProductCacheRef.current = mainProduct;
      processProductData(mainProduct);
      if (failedVariants?.length) {
        setVariants(prev => [...prev, ...failedVariants]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load product');
      console.error('[useProductFormV3] Error reloading product:', err);
      if (!silent) setIsLoading(false);
    }
  }, [productId, processProductData]);

  const appendVariants = useCallback((newVariants: VariantV3[]) => {
    if (!newVariants.length) return;
    setVariants(prev => {
      const existingUuids = new Set(prev.map(v => v.uuid));
      const toAppend = newVariants.filter(v => !existingUuids.has(v.uuid));
      return toAppend.length > 0 ? [...prev, ...toAppend] : prev;
    });
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Return
  // ─────────────────────────────────────────────────────────────────────────

  return {
    // State
    product,
    isLoading,
    isEditMode,
    error,

    // Computed
    isDirty,
    variants,
    variantGroups,

    // Original variant UUIDs for detecting deletions during submission
    originalVariantUuids: originalVariantUuidsRef.current,

    // Field setter
    setField,

    // Axes
    setAxisValues,
    getAxisValues,
    getAllAxisValues,

    // Variants
    regenerateVariants,
    assignAxisValueToVariants,
    setVariantField,
    deleteVariant,
    setDefaultVariant,
    selectVariant,
    selectAllVariants,
    reorderVariants,

    // Visualization
    setVisualizationField,

    // Variant patcher (gallery images, etc.)
    patchVariant,

    // Form actions
    reset,
    reload,
    appendVariants,
  };
}

export default useProductFormV3;
